import { useState, useCallback, useEffect, useRef } from 'react';
import Form, { Item, Label, ButtonItem, ButtonOptions, RequiredRule, FormTypes } from 'devextreme-react/form';
import LoadIndicator from 'devextreme-react/load-indicator';
import { formatMessage } from 'devextreme/localization';
import { useAppSelector, useAppDispatch } from '../../../contexts/hooks';
import { setLoading } from '../../../contexts/slices/auth';
import useApi from './LoginForm.api';

import { LoginStruct } from '../../../types/types.user';
import { useApiInfo } from '../../../api/api.info';
import { appVersion } from '../../../version';
import { runApWithoutToken } from '../../../utils/axios-pettitions';

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const formData: LoginStruct = { User: '', Password: '' };

export const LoginForm = () => {
  const [formInstance, setCormInstance] = useState<FormTypes.InitializedEvent>();
  const { login } = useApi();
  const dispatch = useAppDispatch();
  const [lstLocations, setLstLocation] = useState<any>([]);
  const loading = useAppSelector((data) => data.userInfo).Loading;
  const isAuthenticated = useAppSelector((data) => data.configuration.IsAuthenticated);
  const Locations = useAppSelector((data) => data.userInfo).PermissionsCampus;
  const systemLocations = useAppSelector((data) => data.configuration.Data?.ComboBox)?.filter((x) => x.Name === 'SysI_Sedes')[0].Data;
  const labName = useAppSelector((data) => data.configuration.Data?.Config_General.filter((x) => x.Cgn_Nombre === 'IntitutionName')[0])?.Cgn_Resultado || '';
  const formRef = useRef<Form>(null);
  const { getInfo } = useApiInfo();
  const [infoVersion, setInfoVersion] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string>('');
  const [isTrustedIP, setIsTrustedIP] = useState<boolean>(false);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);

  // Verificar si la IP es de confianza y cargar config de Turnstile
  useEffect(() => {
    const init = async () => {
      try {
        // Verificar IP de confianza - el backend compara la IP del request contra las IPs registradas
        const ipRes = await runApWithoutToken('Authentication/GetTrustedIPs', 'GET');
        if (ipRes.internalCode < 200 && Array.isArray(ipRes.data) && ipRes.data.length > 0) {
          setIsTrustedIP(true);
          return; // IP de confianza, no cargar captcha
        }
      } catch {}

      // IP no confiable, cargar captcha
      try {
        const configRes = await fetch('/config.json');
        const config = await configRes.json();
        const siteKey = config.turnstileSiteKey;
        if (!siteKey) return;
        setTurnstileSiteKey(siteKey);

        if (document.getElementById('cf-turnstile-script')) return;
        const script = document.createElement('script');
        script.id = 'cf-turnstile-script';
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        document.head.appendChild(script);
      } catch {}
    };

    init();
  }, []);

  // Renderizar widget cuando el script esté listo y tengamos el siteKey
  useEffect(() => {
    if (!turnstileSiteKey || !turnstileRef.current) return;

    const renderWidget = () => {
      if (!window.turnstile || !turnstileRef.current) return;
      // Limpiar widget anterior si existe
      if (turnstileWidgetId.current) {
        try { window.turnstile.remove(turnstileWidgetId.current); } catch {}
      }
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token: string) => setCaptchaToken(token),
        'expired-callback': () => setCaptchaToken(null),
        'error-callback': () => setCaptchaToken(null),
        theme: 'light',
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      const script = document.getElementById('cf-turnstile-script');
      if (script) script.addEventListener('load', renderWidget);
    }

    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        try { window.turnstile.remove(turnstileWidgetId.current); } catch {}
        turnstileWidgetId.current = null;
      }
    };
  }, [turnstileSiteKey]);

  // Efecto que se ejecuta solo al montar el componente para limpiar loading
  useEffect(() => {
    // Limpiar el estado de loading al cargar/recargar la página
    dispatch(setLoading(false));
  }, []); // Array vacío significa que solo se ejecuta una vez al montar

  useEffect(() => {
    const form = formRef.current?.instance;
    if (form) {
      form.updateData('User', '');
      form.updateData('Password', '');
      form.updateData('Campus', null);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (Locations && Locations.length > 1) {
      if (systemLocations) {
        const filteredLocations = Locations.map((location) => location.Rus_Sd_Id);
        const nuevaLista = systemLocations.filter((item) => filteredLocations.includes(item.Sd_Id));
        setLstLocation(nuevaLista);
        const campusInstance = formInstance?.component?.getEditor('Campus');
        if (campusInstance) {
          campusInstance.focus();
        }
      }
    }
  }, [Locations]);

  useEffect(() => {
    const userInstance = formInstance?.component?.getEditor('User');
    if (userInstance) {
      userInstance.focus();
    }
  }, [formInstance]);

  useEffect(() => {
    getInfo()
      .then((res) => {
        setInfoVersion(res);
      })
      .catch((error) => {
        console.error('Error fetching version info:', error);
      });
  }, []);

  // Función para deshabilitar el autocompletado después del renderizado
  useEffect(() => {
    const timer = setTimeout(() => {
      // Buscar todos los inputs de password y aplicar atributos adicionales
      const passwordInputs = document.querySelectorAll('input[type="password"]');
      passwordInputs.forEach((input) => {
        input.setAttribute('autocomplete', 'new-password');
        input.setAttribute('data-form-type', 'other');
      });

      // También aplicar a inputs de texto que puedan ser detectados como usuario
      const textInputs = document.querySelectorAll('input[type="text"]');
      textInputs.forEach((input: any) => {
        if (input.name === 'User' || input.getAttribute('data-field') === 'User') {
          input.setAttribute('autocomplete', 'off');
          input.setAttribute('data-form-type', 'other');
        }
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [formInstance]);

  const onInitialized = (e: FormTypes.InitializedEvent) => {
    setCormInstance(e);
  };

  const resetTurnstile = useCallback(() => {
    setCaptchaToken(null);
    if (turnstileWidgetId.current && window.turnstile) {
      try { window.turnstile.reset(turnstileWidgetId.current); } catch {}
    }
  }, []);

  const captchaRequired = !!turnstileSiteKey && !isTrustedIP;

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (captchaRequired && !captchaToken) {
        return;
      }

      try {
        // Hacer una copia de los datos antes de enviar
        const loginData = { ...formData, CaptchaToken: captchaRequired ? captchaToken : null };
        await login(loginData);
      } catch (error) {
        // En caso de error, también limpiar para seguridad
        resetTurnstile();
        setTimeout(() => {
          const form = formRef.current?.instance;
          if (form) {
            form.updateData('Password', '');
          }
        }, 100);
      }
    },
    [login, captchaToken, captchaRequired, resetTurnstile]
  );

  const changePasswordMode = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div>
      {/* Múltiples campos señuelo para confundir a los navegadores */}
      <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }}>
        <input type='text' name='fakeusernameremembered' tabIndex={-1} />
        <input type='password' name='fakepasswordremembered' tabIndex={-1} />
      </div>

      <form className='login-form' onSubmit={onSubmit} autoComplete='off' data-form-type='login'>
        {/* Campo tipo contraseña engaño mejorado */}
        <input
          type='password'
          name='password_fake'
          style={{
            display: 'none',
            position: 'absolute',
            left: '-9999px',
            top: '-9999px',
          }}
          tabIndex={-1}
          autoComplete='new-password'
        />

        <Form onInitialized={onInitialized} focusStateEnabled={true} ref={formRef} showValidationSummary={true} formData={formData} disabled={loading ? loading : false} showColonAfterLabel showRequiredMark={false}>
          <Item
            dataField='User'
            editorType='dxTextBox'
            editorOptions={{
              stylingMode: 'contained',
              labelMode: 'floating',
              placeholder: formatMessage('UserName'),
              // Opciones específicas para evitar autocompletado
              inputAttr: {
                autocomplete: 'off',
                'data-form-type': 'other',
                'data-lpignore': 'true', // Para LastPass
              },
            }}>
            <RequiredRule message={formatMessage('UserRequired')} />
            <Label visible={false} text={formatMessage('UserName')} />
          </Item>

          <Item
            dataField='Password'
            editorType='dxTextBox'
            editorOptions={{
              stylingMode: 'contained',
              labelMode: 'floating',
              placeholder: formatMessage('Password'),
              mode: showPassword ? 'text' : 'password',
              buttons: [
                {
                  name: 'password',
                  location: 'after',
                  options: {
                    stylingMode: 'text',
                    icon: 'eyeopen',
                    onClick: () => changePasswordMode(),
                  },
                },
              ],
              // Opciones específicas para evitar guardado de contraseña
              inputAttr: {
                autocomplete: 'new-password',
                'data-form-type': 'other',
                'data-lpignore': 'true', // Para LastPass
              },
            }}>
            <RequiredRule message={formatMessage('PasswordRequired')} />
            <Label visible={false} text={formatMessage('Password')} />
          </Item>

          {Locations && Locations.length > 1 && (
            <Item
              dataField='Campus'
              editorType='dxSelectBox'
              editorOptions={{
                searchTimeout: 0,
                items: lstLocations,
                displayExpr: 'Sd_Nombre',
                valueExpr: 'Sd_Id',
                searchEnabled: true,
                stylingMode: 'contained',
                labelMode: 'floating',
                placeholder: formatMessage('Location'),
                inputAttr: {
                  autocomplete: 'off',
                },
              }}>
              <RequiredRule message={formatMessage('LocationRequired')} />
              <Label visible={false} text={formatMessage('Location')} />
            </Item>
          )}

          <ButtonItem>
            <ButtonOptions width='100%' type='default' useSubmitBehavior disabled={captchaRequired && !captchaToken}>
              <span className='dx-button-text'>{loading ? <LoadIndicator width='24px' height='24px' visible /> : formatMessage('ButtonLogin').toUpperCase()}</span>
            </ButtonOptions>
          </ButtonItem>
        </Form>
        {captchaRequired && (
          <div ref={turnstileRef} style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }} />
        )}
      </form>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '16px', width: '100%' }}>
        <strong style={{ fontSize: '12px', color: '#888' }}>{labName}</strong>
        <strong style={{ fontSize: '12px', color: '#888' }}>
          V. Core: {infoVersion} - App: {appVersion}
        </strong>
      </div>
    </div>
  );
};
